// client/src/pages/secure/MyWebsiteLeadsPage.js — the agent's side of the website assistant.
//
//   My leads     website-chat requests routed to me (or for clients in my book): the
//                transcript, the details the visitor gave with consent, and a reply.
//   My outbox    drafts the assistant prepared and anything held for my clients; I send them.
//   Assistant    the portal assistant: my follow-ups, a client's history, listing copy,
//                "how do I…" — limited to my own book by the server.
//   Preferences  signature, how lead alerts reach me (email / text), quiet hours, out of office.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { OutboxList, LintBox, StatusPill } from '../../components/secure/OutboundPanels';
import PortalAssistantPanel from '../../components/secure/PortalAssistantPanel';

const API = '/api/chat-admin/my';
const card = 'bg-white rounded-lg shadow p-4';
const btn = 'px-3 py-1.5 rounded text-sm';
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

function Leads({ api, initial }) {
    const [rows, setRows] = useState([]);
    const [sel, setSel] = useState(null);
    const [conv, setConv] = useState(null);
    const [reply, setReply] = useState({ channel: 'email', subject: '', body: '' });
    const [msg, setMsg] = useState('');
    useEffect(() => { api.get(`${API}/leads`).then(setRows).catch((e) => setMsg(e.message)); }, [api]);
    const open = useCallback((id) => { setSel(id); setConv(null); api.get(`${API}/conversations/${id}`).then(setConv).catch((e) => setMsg(e.message)); }, [api]);
    // The link in a lead alert opens the conversation straight away.
    useEffect(() => { if (initial) open(Number(initial)); }, [initial, open]);
    const [last, setLast] = useState(null);
    const send = async (e) => {
        e.preventDefault(); setMsg(''); setLast(null);
        try {
            const r = await api.post(`${API}/conversations/${sel}/reply`, reply);
            setLast(r);
            setMsg(r.status === 'sent' ? `Sent (O-${r.message_id}).`
                : r.status === 'scheduled' ? `Held until ${fmt(r.next_attempt_at)} — the recipient's permitted hours (O-${r.message_id}).`
                : r.status === 'blocked' ? `Not sent — Contact Compliance: ${((r.compliance && r.compliance.blocked_by) || []).join(', ')}.`
                : `O-${r.message_id}: ${r.status}${r.error ? ` — ${r.error}` : ''}`);
            if (r.status === 'sent' || r.status === 'scheduled') setReply({ ...reply, body: '' });
        } catch (e2) { setMsg(e2.message); }
    };
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className={card}>
                <h3 className="font-semibold text-sm mb-2">Website requests for me</h3>
                {rows.length === 0 && <p className="text-sm text-slate-500">None open.</p>}
                <ul className="space-y-2">
                    {rows.map((h) => (
                        <li key={h.handoff_id}>
                            <button onClick={() => open(h.conversation_id)} className={`w-full text-left text-sm border rounded p-2 ${sel === h.conversation_id ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                                <div className="font-medium">{h.request_type} · {h.client_name || 'visitor (no details yet)'}</div>
                                {h.street_address && <div className="text-xs">{h.street_address}, {h.city}</div>}
                                {h.preferred_time && <div className="text-xs">Prefers: {h.preferred_time}</div>}
                                <div className="text-xs text-slate-500">{fmt(h.created_at)}</div>
                            </button>
                            {h.request_type === 'valuation' && (
                                <Link to={`/dashboard/cma?new=1&handoff_id=${h.handoff_id}`} className="text-xs underline text-indigo-700 ml-2">Prepare CMA</Link>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
            <div className={`${card} lg:col-span-2 space-y-3`}>
                {msg && <p className="text-sm bg-slate-100 rounded p-2" role="status">{last && <StatusPill s={last.status} />} {msg}</p>}
                {!sel && <p className="text-sm text-slate-500">Pick a request.</p>}
                {conv && (
                    <>
                        {conv.client && (
                            <div className="text-sm border rounded p-2 bg-slate-50">
                                <b>{conv.client.first_name} {conv.client.last_name}</b> · {conv.client.client_type} · {conv.client.pipeline_stage}
                                <div>{conv.client.email || 'no email'} · {conv.client.phone_number || 'no phone'}</div>
                                {conv.conversation.consent_at && <div className="text-xs text-slate-500">Consent to be contacted given in the chat {fmt(conv.conversation.consent_at)}</div>}
                            </div>
                        )}
                        <ul className="space-y-1 max-h-80 overflow-y-auto border rounded p-2">
                            {conv.messages.map((m) => (
                                <li key={m.message_id} className="text-sm"><b className={m.sender === 'guest' ? 'text-indigo-700' : 'text-slate-600'}>{m.sender === 'guest' ? 'Visitor' : m.sender === 'staff' ? 'Staff' : 'Assistant'}:</b> <span className="whitespace-pre-line">{m.body}</span></li>
                            ))}
                        </ul>
                        {conv.client && (
                            <form onSubmit={send} className="space-y-2 text-sm">
                                <div className="flex gap-2">
                                    <select value={reply.channel} onChange={(e) => setReply({ ...reply, channel: e.target.value })} className="p-1 border rounded" aria-label="Channel">
                                        <option value="email">Email</option><option value="sms">Text</option>
                                    </select>
                                    {reply.channel === 'email' && <input className="flex-1 p-1 border rounded" placeholder="Subject" value={reply.subject} onChange={(e) => setReply({ ...reply, subject: e.target.value })} aria-label="Subject" />}
                                </div>
                                <textarea className="w-full p-2 border rounded" rows="4" placeholder="Your reply" value={reply.body} onChange={(e) => setReply({ ...reply, body: e.target.value })} aria-label="Reply" />
                                <div className="flex items-center gap-2">
                                    <button className={`${btn} bg-slate-800 text-white`}>Send now</button>
                                    <span className="text-xs text-slate-500">Sent as you, with your identification block, after the opt-out / consent / hours check.</span>
                                </div>
                            </form>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function Prefs({ api }) {
    const [p, setP] = useState(null);
    const [ooo, setOoo] = useState({ from: '', to: '' });
    const [msg, setMsg] = useState('');
    useEffect(() => {
        api.get(`${API}/prefs`).then((x) => {
            setP(x);
            setOoo({ from: x.out_of_office?.out_of_office_from ? String(x.out_of_office.out_of_office_from).slice(0, 10) : '',
                to: x.out_of_office?.out_of_office_to ? String(x.out_of_office.out_of_office_to).slice(0, 10) : '' });
        }).catch((e) => setMsg(e.message));
    }, [api]);
    if (!p) return <p className="text-sm text-slate-500">{msg || 'Loading…'}</p>;
    const pr = p.prefs;
    const set = (k, v) => setP({ ...p, prefs: { ...pr, [k]: v } });
    const save = async () => {
        setMsg('');
        const body = { ...pr, notify_email: !!Number(pr.notify_email), notify_sms: !!Number(pr.notify_sms),
            show_lead_alerts: !!Number(pr.show_lead_alerts ?? 1) };
        if (p.has_agent_record) { body.out_of_office_from = ooo.from || null; body.out_of_office_to = ooo.to || null; }
        try { await api.put(`${API}/prefs`, body); setMsg('Saved.'); } catch (e) { setMsg(e.message); }
    };
    return (
        <div className={`${card} space-y-4 text-sm max-w-xl`}>
            <label className="block">Signature for drafts
                <textarea rows="3" maxLength={500} value={pr.signature || ''} onChange={(e) => set('signature', e.target.value)} className="block w-full p-2 border rounded" /></label>
            <fieldset className="space-y-2">
                <legend className="font-semibold">When a website lead is routed to me</legend>
                <label className="flex gap-2 items-center"><input type="checkbox" checked={!!Number(pr.notify_email)} onChange={(e) => set('notify_email', e.target.checked ? 1 : 0)} />Email me the details and transcript</label>
                <label className="flex gap-2 items-center"><input type="checkbox" checked={!!Number(pr.notify_sms)} onChange={(e) => set('notify_sms', e.target.checked ? 1 : 0)} disabled={!p.sms_available && !Number(pr.notify_sms)} />
                    Text me a short alert {!p.sms_available && <span className="text-xs text-slate-500">(texting is not set up for this office yet)</span>}</label>
                <label className="block">Mobile number for alerts
                    <input type="tel" value={pr.notify_mobile || ''} onChange={(e) => set('notify_mobile', e.target.value)} className="block w-48 p-1 border rounded" autoComplete="tel" /></label>
                <div className="flex flex-wrap gap-3 items-end">
                    <label>Quiet hours from<input type="time" value={pr.quiet_start || ''} onChange={(e) => set('quiet_start', e.target.value)} className="block p-1 border rounded" /></label>
                    <label>to<input type="time" value={pr.quiet_end || ''} onChange={(e) => set('quiet_end', e.target.value)} className="block p-1 border rounded" /></label>
                    <label>Time zone<input value={pr.agent_timezone || ''} placeholder="office time zone" onChange={(e) => set('agent_timezone', e.target.value)} className="block w-44 p-1 border rounded" /></label>
                </div>
                <p className="text-xs text-slate-500">Alerts that arrive during quiet hours are held until they end. Whether alerts go out by themselves depends on the office's sending settings.</p>
            </fieldset>
            {p.has_agent_record && (
                <fieldset className="space-y-2">
                    <legend className="font-semibold">Out of office</legend>
                    <div className="flex flex-wrap gap-3 items-end">
                        <label>From<input type="date" value={ooo.from} onChange={(e) => setOoo({ ...ooo, from: e.target.value })} className="block p-1 border rounded" /></label>
                        <label>To<input type="date" value={ooo.to} onChange={(e) => setOoo({ ...ooo, to: e.target.value })} className="block p-1 border rounded" /></label>
                    </div>
                    <p className="text-xs text-slate-500">Lead routing skips you on these dates, and alerts for leads already yours go to the managing broker.</p>
                </fieldset>
            )}
            <button className={`${btn} bg-slate-800 text-white`} onClick={save}>Save</button> {msg && <span role="status">{msg}</span>}
        </div>
    );
}

const MyWebsiteLeadsPage = () => {
    const api = useApiClient();
    const [params, setParams] = useSearchParams();
    const tabs = [['leads', 'My website leads'], ['outbox', 'My outbox'], ['assistant', 'Assistant'], ['check', 'Fair-housing check'], ['prefs', 'Preferences']];
    const tab = tabs.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'leads';
    const conversation = params.get('conversation');
    return (
        <div className="space-y-4">
            <h1 className="text-2xl font-bold text-slate-800">My Website Leads &amp; Assistant</h1>
            <div className="flex gap-1 border-b" role="tablist">
                {tabs.map(([k, label]) => (
                    <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams({ tab: k })}
                        className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-slate-700 text-indigo-700 font-medium' : 'border-transparent text-slate-600'}`}>{label}</button>
                ))}
            </div>
            {tab === 'leads' && <Leads api={api} initial={conversation} />}
            {tab === 'outbox' && <OutboxList api={api} mine />}
            {tab === 'check' && <LintBox api={api} />}
            {tab === 'assistant' && <PortalAssistantPanel api={api} />}
            {tab === 'prefs' && <Prefs api={api} />}
        </div>
    );
};

export default MyWebsiteLeadsPage;
